import { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../../../firebase/firestore";
import {
  collection,
  onSnapshot,
  addDoc,
  query
} from "firebase/firestore";
import { CLOUD_NAME, UPLOAD_PRESET } from "../../../utils/cloudinary";

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (Number.isFinite(value?.seconds)) return Number(value.seconds) * 1000;
  if (typeof value === "number") return value;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeDisplayName(rawName) {
  const value = String(rawName || "").trim();
  if (!value) return "";

  const lowered = value.toLowerCase();
  const guestLike = new Set([
    "guest",
    "guest user",
    "anonymous",
    "anonymous user",
    "user",
    "new user",
    "customer"
  ]);

  return guestLike.has(lowered) ? "" : value;
}

export default function Support() {
  const [messages, setMessages] = useState([]);
  const [userDirectory, setUserDirectory] = useState({});
  const [reply, setReply] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [showUserList, setShowUserList] = useState(true);
  const [unread, setUnread] = useState({});
  const [chatLoading, setChatLoading] = useState(true);
  const [chatError, setChatError] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [actionStatus, setActionStatus] = useState("");
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 1024px)").matches);
  const bottomRef = useRef();
  const lastNotificationIdRef = useRef("");

  const MAX_IMAGE_SIZE = 3 * 1024 * 1024;
  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

  // 🔥 USER DIRECTORY
  useEffect(() => {
    const unsubUsers = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          const info = d.data() || {};
          map[d.id] = {
            name: String(info.name || "").trim(),
            email: String(info.email || "").trim(),
            phone: String(info.phone || "").trim(),
          };
        });
        setUserDirectory(map);
      },
      () => {
        // Keep current cache if user directory read fails.
      }
    );

    return () => unsubUsers();
  }, []);

  // 🔥 REALTIME FETCH
  useEffect(() => {
    setChatLoading(true);
    setChatError("");

    const q = query(collection(db, "support"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .sort((a, b) => toMillis(a.createdAtMs || a.createdAt) - toMillis(b.createdAtMs || b.createdAt));

        const unreadMap = {};
        data.forEach((m) => {
          const userId = String(m.userId || "").trim();
          if (!userId || m.fromAdmin) return;
          unreadMap[userId] = (unreadMap[userId] || 0) + 1;
        });

        setUnread(unreadMap);
        setMessages(data);
        setChatLoading(false);
        setChatError("");

        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          const last = data[data.length - 1];
          if (last && !last.fromAdmin && last.id !== lastNotificationIdRef.current) {
            lastNotificationIdRef.current = last.id;
            new Notification("New Message", {
              body: String(last.message || "New support message"),
            });
          }
        }
      },
      () => {
        setChatLoading(false);
        setChatError("Realtime chat is unavailable right now. Please retry.");
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1024px)");
    const onChange = (event) => {
      setIsMobile(event.matches);
      if (!event.matches) {
        setShowUserList(true);
      }
    };

    onChange(media);
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  // 🔔 REQUEST PERMISSION
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // 🔥 AUTO SCROLL
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, selectedUserId]);

  const conversations = useMemo(() => {
    const byUser = {};

    messages.forEach((m) => {
      const userId = String(m.userId || "").trim();
      if (!userId) return;

      const createdAtMs = toMillis(m.createdAtMs || m.createdAt) || Date.now();

      const fallbackName = `User ${userId.slice(0, 6)}`;
      const userInfo = userDirectory[userId] || {};
      const resolvedName =
        normalizeDisplayName(userInfo.name) ||
        normalizeDisplayName(m.name) ||
        (userInfo.email ? String(userInfo.email).split("@")[0] : "") ||
        fallbackName;

      byUser[userId] = {
        userId,
        name: resolvedName,
        email: userInfo.email || "",
        phone: String(m.phone || userInfo.phone || "").replace(/[^0-9]/g, "").slice(-10),
        lastMessage: String(m.message || (m.image ? "Image" : "")).slice(0, 70),
        lastAt: createdAtMs,
      };
    });

    return Object.values(byUser).sort((a, b) => b.lastAt - a.lastAt);
  }, [messages, userDirectory]);

  const selectedConversation = useMemo(
    () => conversations.find((u) => u.userId === selectedUserId) || null,
    [conversations, selectedUserId]
  );

  const selectedMessages = useMemo(
    () =>
      messages
        .filter((m) => String(m.userId || "") === selectedUserId)
        .sort((a, b) => toMillis(a.createdAtMs || a.createdAt) - toMillis(b.createdAtMs || b.createdAt)),
    [messages, selectedUserId]
  );

  useEffect(() => {
    if (!selectedUserId && conversations.length) {
      setSelectedUserId(conversations[0].userId);
    }
  }, [conversations, selectedUserId]);

  // 🔥 SEND MESSAGE
  const sendReply = async () => {
    const trimmed = reply.trim();
    if (!trimmed || !selectedConversation || sendingReply) return;

    setSendingReply(true);
    setActionStatus("");
    try {
      await addDoc(collection(db, "support"), {
        userId: selectedConversation.userId,
        name: selectedConversation.name,
        message: trimmed,
        fromAdmin: true,
        createdAt: new Date().toISOString(),
        createdAtMs: Date.now(),
      });

      setReply("");
    } catch {
      setActionStatus("Could not send message. Please retry.");
    } finally {
      setSendingReply(false);
    }
  };

  // 📸 IMAGE UPLOAD
  const uploadImage = async (file) => {
    if (!file || !selectedConversation) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      alert("Only JPG, PNG or WEBP files are allowed");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      alert("Image must be smaller than 3MB");
      return;
    }

    setUploadingImage(true);
    setActionStatus("");

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("upload_preset", UPLOAD_PRESET);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method: "POST", body: form }
      );
      const data = await res.json();
      if (!res.ok || !data?.secure_url) {
        throw new Error("upload-failed");
      }

      await addDoc(collection(db, "support"), {
        userId: selectedConversation.userId,
        name: selectedConversation.name,
        image: data.secure_url,
        message: "",
        fromAdmin: true,
        createdAt: new Date().toISOString(),
        createdAtMs: Date.now(),
      });
    } catch {
      setActionStatus("Image upload failed. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  // 📍 SEND LOCATION
  const sendLocation = async () => {
    if (!selectedConversation) return;
    const link = "https://maps.google.com?q=21.1702,72.8311";

    try {
      await addDoc(collection(db, "support"), {
        userId: selectedConversation.userId,
        name: selectedConversation.name,
        message: `📍 Location: ${link}`,
        fromAdmin: true,
        createdAt: new Date().toISOString(),
        createdAtMs: Date.now(),
      });
    } catch {
      setActionStatus("Could not send location.");
    }
  };

  return (
    <div className="support-page">

      <h1>💬 Customer Support</h1>

      <div className="support-container">

        {/* 🔥 USER LIST */}
        <div className={`user-list ${showUserList ? "show" : "hide"}`}>
          <div className="support-list-head">
            <h3>Conversations</h3>
          </div>
          {!conversations.length ? (
            <p className="support-empty-list">No chats yet</p>
          ) : (
            conversations.map((u) => (
              <button
                type="button"
                key={u.userId}
                className={`user-card ${selectedUserId === u.userId ? "active" : ""}`}
                onClick={() => {
                  setSelectedUserId(u.userId);
                  if (isMobile) {
                    setShowUserList(false);
                  }
                }}
              >
                <div className="row-between">
                  <b>{u.name || "User"}</b>
                  {unread[u.userId] ? <span className="badge">{unread[u.userId]}</span> : null}
                </div>
                <p>{u.lastMessage || "No message"}</p>
              </button>
            ))
          )}
        </div>

        {/* 🔥 CHAT */}
        <div className="chat-section">
          <div className="support-mobile-toggle-wrap">
            <button type="button" className="support-mobile-toggle" onClick={() => setShowUserList((prev) => !prev)}>
              {showUserList ? "Hide Users" : "Show Users"}
            </button>
          </div>

          {!selectedConversation ? (
            <div className="support-empty">Select user</div>
          ) : (
            <>
              {/* HEADER */}
              <div className="chat-header">
                <div>
                  <strong>👤 {selectedConversation.name}</strong>
                  <div className="chat-meta">{selectedConversation.email || selectedConversation.userId}</div>
                </div>

                {isMobile ? (
                  <button type="button" className="support-mobile-toggle" onClick={() => setShowUserList(true)}>
                    Back
                  </button>
                ) : null}

                {/* 📞 CALL */}
                <a
                  href={selectedConversation.phone ? `tel:+91${selectedConversation.phone}` : "tel:"}
                  className="call-btn"
                  aria-disabled={!selectedConversation.phone}
                >
                  📞
                </a>
              </div>

              {/* MESSAGES */}
              <div className="chat-messages">
                {chatLoading ? <p className="support-empty">Loading chat...</p> : null}
                {!chatLoading && chatError ? <p className="support-error">{chatError}</p> : null}
                {selectedMessages
                  .map((m) => (
                    <div
                      key={m.id}
                      className={m.fromAdmin ? "msg admin" : "msg user"}
                    >
                      {m.image && (
                        <img src={m.image} className="chat-img" alt="img"/>
                      )}
                      {m.message ? <p>{m.message}</p> : null}
                      <small className="chat-time">{new Date(toMillis(m.createdAtMs || m.createdAt) || Date.now()).toLocaleString()}</small>
                    </div>
                  ))}
                <div ref={bottomRef} />
              </div>

              {/* INPUT */}
              <div className="chat-input">
                <input
                  placeholder="Type your reply..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      sendReply();
                    }
                  }}
                />

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      uploadImage(file);
                    }
                    e.target.value = "";
                  }}
                />

                <button onClick={sendLocation}>📍</button>

                <button onClick={sendReply} disabled={sendingReply}>{sendingReply ? "Sending..." : "Send"}</button>
              </div>
              {uploadingImage ? <p className="support-status">Uploading image...</p> : null}
              {actionStatus ? <p className="support-error">{actionStatus}</p> : null}
            </>
          )}

        </div>
      </div>
    </div>
  );
}